import { useState } from 'react';
import { addLeadNote } from '../api/leadNotes';
import type { LeadNote } from '../types';

/**
 * Hook for adding a new note to a lead
 * @param leadId - The ID of the lead
 * @returns Mutation function, loading state, error state, and reset function
 */
export function useAddLeadNote(leadId: number) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = async (noteText: string): Promise<LeadNote> => {
    setIsLoading(true);
    setError(null);
    try {
      const note = await addLeadNote(leadId, noteText);
      return note;
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage =
        error.response?.data?.message || 'Failed to add note';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setError(null);
  };

  return {
    mutate,
    isLoading,
    error,
    reset,
  };
}
